// src/pages/ApproveOrder.js
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  runTransaction,
  serverTimestamp,
  addDoc,
  query,
  where,
  writeBatch,
  increment
} from "firebase/firestore";
import { generateTripSheetPdf } from "../utils/pdfTripSheet";

import { db } from "../firebase";
import Layout from "../components/Layout";
import { buildHsnSummary } from "../utils/hsnSummary";
import { generateChallanPdf } from "../utils/pdfChallan";

import { supabase } from "../supabase";

import { syncLedgerOrderAmount, getISTDateString } from "../services/cashierService";
import { normalizeAndMergeItems, splitOrderToOutlets, calculateOrderTotals } from "../utils/orderUtils";
import { processSingleOrderApproval } from "../services/orderApprovalService";

// =========================
// ACTION BAR & BUTTON UI
// =========================
// --- PREMIUM UI TOKENS ---
const UI_THEME = {
  bg: "#f8fafc", // slate-50
  surface: "#ffffff",
  primary: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)", // indigo
  secondary: "#f1f5f9", // slate-100
  accent: "#10b981", // emerald-500
  text: "#0f172a", // slate-900
  muted: "#64748b", // slate-500
  border: "#e2e8f0", // slate-200
  shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  glass: "rgba(255, 255, 255, 0.85)",
};

const actionBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px 32px",
  background: UI_THEME.glass,
  backdropFilter: "blur(12px)",
  borderBottom: `1px solid ${UI_THEME.border}`,
  position: "sticky",
  top: 0,
  zIndex: 10,
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
};

const cardStyle = {
  background: UI_THEME.surface,
  borderRadius: "16px",
  boxShadow: UI_THEME.shadow,
  border: `1px solid ${UI_THEME.border}`,
  padding: "24px",
  marginBottom: "32px",
};

const baseBtn = {
  padding: "10px 20px",
  fontSize: "14px",
  fontWeight: 700,
  borderRadius: "12px",
  cursor: "pointer",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
};

const primaryBtn = {
  ...baseBtn,
  background: UI_THEME.primary,
  color: "#fff",
  border: "none",
  boxShadow: "0 4px 14px 0 rgba(79, 70, 229, 0.39)",
};

const secondaryBtn = {
  ...baseBtn,
  background: UI_THEME.surface,
  color: "#4f46e5",
  border: `1px solid #4f46e5`,
  boxShadow: "0 2px 4px 0 rgba(79, 70, 229, 0.1)",
};

const utilityBtn = {
  ...baseBtn,
  background: "#f1f5f9",
  color: "#475569",
  border: "none",
  fontSize: "13px",
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  background: "#fff",
  padding: "30px",
  borderRadius: "16px",
  width: "500px",
  maxWidth: "90%",
  maxHeight: "80vh",
  overflowY: "auto",
  boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
};

const openWhatsApp = (phone, message) => {
  if (!phone) {
    alert("Party phone number not available");
    return;
  }

  const cleanPhone = phone.replace(/\D/g, ""); // only numbers
  const encodedMsg = encodeURIComponent(message);

  const url = `https://wa.me/${cleanPhone}?text=${encodedMsg}`;
  window.open(url, "_blank");
};





// ---------- Helpers ----------
function formatYYMMDD(date) {
  const yy = String(date.getFullYear()).slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}${mm}${dd}`;
}

function getFinancialYear(date) {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed (0=Jan, 3=Apr)
  if (month < 3) {
    return String(year - 1).slice(-2) + String(year).slice(-2);
  } else {
    return String(year).slice(-2) + String(year + 1).slice(-2);
  }
}

const blobFromPdf = (pdfDoc) => {
  return pdfDoc.output("blob");
};


function buildFinalOrderId(clientId, dateStr, seqNumber) {
  const prefix = (clientId || "MD").slice(0, 2).toUpperCase();
  return `${prefix}-${dateStr}-${String(seqNumber).padStart(4, "0")}`;
}

// --- SPLIT ENGINE LOGIC REMOVED - IMPORTED FROM orderUtils ---

function ApproveOrder() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const autoDownloadPdf = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  
  
  
  
  
  
  
  
  
  
  
  
  
  
  // small press animation for buttons
const pressAnim = {
  onMouseDown: (e) =>
    (e.currentTarget.style.transform = "scale(0.96)"),
  onMouseUp: (e) =>
    (e.currentTarget.style.transform = "scale(1)"),
  onMouseLeave: (e) =>
    (e.currentTarget.style.transform = "scale(1)"),
};


  const [skuMaster, setSkuMaster] = useState({});
  const [items, setItems] = useState({});
  const [order, setOrder] = useState(null);
  const [company, setCompany] = useState(null);
  const [vehicleNo, setVehicleno] = useState("-");
  const [displayOrderId, setDisplayOrderId] = useState("");
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  // --- NEW STATES FOR OUTLET BILLING LAYER ---
  const [outlets, setOutlets] = useState([]);
  const [selectedOutletIds, setSelectedOutletIds] = useState([]);
  const [showOutletModal, setShowOutletModal] = useState(false);
  const [skuListOrdered, setSkuListOrdered] = useState([]);
  const [address, setAddress] = useState("");
  const [approvingStep, setApprovingStep] = useState("");
  const [approvalProgress, setApprovalProgress] = useState(0);
  const [activeSkus, setActiveSkus] = useState([]);
  
  // --- NEW STATES FOR SKU SEARCH ---
  const [skuSearch, setSkuSearch] = useState("");
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);

  
  
  
  // ================================
// SUPABASE PDF UPLOAD HELPER
// ================================
const uploadPdfToSupabase = async (bucket, filePath, blob) => {
  if (typeof filePath !== "string") {
    throw new Error("filePath must be a string");
  }

  const { error } = await supabase.storage
    .from(bucket)
    .upload(filePath, blob, {
      contentType: "application/pdf",
      upsert: true,
    });

  if (error) {
    console.error("Supabase upload error:", error);
    throw error;
  }

  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);

  return data.publicUrl;
};
  
  
  const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

  
  
  
  
  // -------------------------
  // Load data
  // -------------------------
  useEffect(() => {
    const loadAll = async () => {
      const skuSnap = await getDocs(collection(db, "skuMaster"));
      
	  
	  
	  
	  const skuArr = [];
const skuMap = {};

skuSnap.forEach((d) => {
  const data = d.data();
  if (!data.sku) return;

  const skuKey = data.sku.trim().toUpperCase();

  skuArr.push({
    sku: skuKey,
    sortorder: Number(data.sortorder ?? 9999), // fallback safety
  });

  skuMap[skuKey] = data;
});

// 🔹 SORT ONCE, BY sortorder
skuArr.sort((a, b) => a.sortorder - b.sortorder);

// 🔹 SAVE BOTH
setSkuMaster(skuMap);
setSkuListOrdered(skuArr.map((x) => x.sku));

	  
	  
	  

      const refOrder = doc(db, "orders", orderId);
      const orderSnap = await getDoc(refOrder);
      if (!orderSnap.exists()) {
        alert("Order not found");
        navigate("/pending-orders");
        return;
      }

      const oData = orderSnap.data();
      setOrder(oData);

      // Init items with ALL SKU master
      const tempItems = {};
      Object.keys(skuMap).forEach((sku) => (tempItems[sku] = 0));
      const initialActiveSkus = [];
      Object.entries(oData.items || {}).forEach(
        ([sku, qty]) => {
          const skuKey = sku.trim().toUpperCase();
          tempItems[skuKey] = Number(qty);
          if (Number(qty) > 0) {
            initialActiveSkus.push(skuKey);
          }
        }
      );
      setItems(tempItems);
      setActiveSkus(initialActiveSkus);

      if (oData.partyId) {
  const pSnap = await getDoc(doc(db, "partyMaster", oData.partyId));
  if (pSnap.exists()) {
    const pData = pSnap.data();
    setVehicleno(pData.vehicleno || "");
    setAddress(pData.address || "");
  }
}

      const cSnap = await getDoc(doc(db, "companyInfo", "default"));
      if (cSnap.exists()) setCompany(cSnap.data());

      setDisplayOrderId(
        oData.displayOrderId || oData.tempOrderId || oData.orderNo || orderId
      );

      // --- FETCH OUTLETS FOR PARTY ---
      if (oData.partyId) {
        const qOutlets = query(collection(db, "outletMaster"), where("partyId", "==", oData.partyId), where("status", "==", "active"));
        const snapOutlets = await getDocs(qOutlets);
        setOutlets(snapOutlets.docs.map(d => ({ id: d.id, ...d.data() })));
      }

      setLoading(false);
    };

    if (orderId) loadAll();
  }, [orderId, navigate]);

  // -------------------------
  // UI VIEW (ALL SKUs)
  const itemsForUI = useMemo(() => {
    const obj = {};
    Object.keys(skuMaster).forEach(
      (sku) => {
        const val = items[sku];
        obj[sku] = (val === undefined || val === "") ? "" : val;
      }
    );
    return obj;
  }, [items, skuMaster]);

  // Derived list of SKUs not yet in the order
  const remainingSkus = useMemo(() => {
    return skuListOrdered.filter(sku => !activeSkus.includes(sku));
  }, [skuListOrdered, activeSkus]);

  // -------------------------
  // SAVE VIEW (MERGED)
  // -------------------------
  const mergedItemsForSave = useMemo(
    () => normalizeAndMergeItems(items),
    [items]
  );

  // -------------------------
  // Update qty
  // -------------------------
  const updateQty = (sku, val) => {
    setItems({ ...items, [sku]: val });
    // Reset selection whenever quantities change to force re-verification in modal
    if (selectedOutletIds.length > 0) {
      setSelectedOutletIds([]);
    }
  };

  // -------------------------
  // Totals FOR DELIVERY CHALLAN
  // -------------------------
  const computeTotals = () => {
    let tQty = 0,
      tBasic = 0,
      tGST = 0,
      tCESS = 0,
      tAddl = 0,
      tGrand = 0;

    Object.entries(mergedItemsForSave).forEach(([sku, qty]) => {
      const m = skuMaster[sku] || {};
      const rate = Number(m.Basic_Eway || 0);
      const BASIC = qty * rate;
      const mrpTotal = qty * (Number(m.mrp) || 0);
	  const GST = mrpTotal - (mrpTotal / (1 + ((Number(m.gst) || 0) / 100)));
      const CESS = BASIC * ((Number(m.cess) || 0) / 100);
      const ADDL = qty * (Number(m.addlcess) || 0);
      const REBATE = qty * (Number(m.Rebate) || 0);

      tQty += qty;
      tBasic += BASIC;
      tGST += GST;
      tCESS += CESS;
      tAddl += ADDL;
      tGrand += BASIC + GST + CESS + ADDL;
    });

    return {
      totalQty: tQty,
      totalBasic: tBasic,
      totalGST: tGST,
      totalCESS: tCESS,
      totalAddlCess: tAddl,
      grandTotal: tGrand,
    };
  };

  const totals = computeTotals();
  useMemo(() => buildHsnSummary(items, skuMaster), [items, skuMaster]);



// Totals FOR TRIPSHEET
  // -------------------------
  const computeTotalsTrip = () => {
    let tQty = 0,
      tBasic = 0,
      tGST = 0,
      tCESS = 0,
      tAddl = 0,
      tGrand = 0;

    Object.entries(mergedItemsForSave).forEach(([sku, qty]) => {
      const m = skuMaster[sku] || {};
      const rate = Number(m.Basic_Eway || 0);
      const BASIC = qty * rate;
      const mrpTotal = qty * (Number(m.mrp) || 0);
	  const GST = mrpTotal - (mrpTotal / (1 + ((Number(m.gst) || 0) / 100)));
      const CESS = BASIC * ((Number(m.cess) || 0) / 100);
      const ADDL = qty * (Number(m.addlcess) || 0);
      const REBATE = qty * (Number(m.Rebate) || 0);

      tQty += qty;
      tBasic += BASIC;
      tGST += GST;
      tCESS += CESS;
      tAddl += ADDL;
      tGrand += BASIC + GST + CESS + ADDL+ REBATE;
    });

    return {
      totalQty: tQty,
      totalBasic: tBasic,
      totalGST: tGST,
      totalCESS: tCESS,
      totalAddlCess: tAddl,
      grandTotal: tGrand,
    };
  };

  const totalstrip = computeTotalsTrip();
  useMemo(() => buildHsnSummary(items, skuMaster), [items, skuMaster]);











  const approveOrder = async () => {
    if (approving) return;

    if (outlets.length > 0 && !showOutletModal) {
       setShowOutletModal(true);
       return;
    }

    if (outlets.length > 0 && selectedOutletIds.length === 0) {
       alert("No outlets selected. Please select at least one outlet to distribute the bill.");
       return;
    }

    try {
      setApproving(true);
      setApprovalProgress(0);
      
      const result = await processSingleOrderApproval(orderId, {
        onStep: (step) => setApprovingStep(step),
        onProgress: (p) => setApprovalProgress(p),
        itemsOverride: items,
        vehicleNoOverride: vehicleNo,
        addressOverride: address,
        selectedOutletIdsOverride: selectedOutletIds
      });

      if (result.success) {
        if (result.status === "ALREADY_APPROVED") {
          alert("⚠️ This order has already been approved.");
        } else {
          alert("Order Approved Successfully!");
          
          // Open Trip Sheet in new tab for immediate review/print
          if (result.tripUrl) {
            window.open(result.tripUrl, "_blank");
          }
        }
        navigate("/pending-orders");
      } else {
        alert("Approve failed: " + result.error);
      }
    } catch (e) {
      console.error(e);
      alert("Approve failed: " + e.message);
    } finally {
      setApproving(false);
      setApprovingStep("");
    }
  };

  const getOrderDateForTripSheet = () => {
    if (order?.orderDate) return order.orderDate;
    if (order?.createdAt?.toDate) return order.createdAt.toDate().toISOString();
    return new Date().toISOString();
  };


// -------------------------
// FINAL PDF GENERATORS
// -------------------------
const generateFinalTripSheet = async () => {
  try {
    const finalId = order.displayOrderId;
    const totals = computeTotals();
    const pdfDoc = generateTripSheetPdf(finalId, order, mergedItemsForSave, skuMaster, totals, "Trip Sheet");
    const blob = pdfDoc.output("blob");
    const path = `TripSheet-${finalId}.pdf`;
    const pdfUrl = await uploadPdfToSupabase("tripsheets", path, blob);
    await updateDoc(doc(db, "orders", orderId), { tripSheetPdfUrl: pdfUrl });
    window.open(pdfUrl, "_blank");
  } catch (err) { alert("Failed to generate Trip Sheet"); }
};

const generateDeliveryChallan = async () => {
  try {
    const finalId = order.displayOrderId;
    const pdfDoc = generateChallanPdf(finalId, { ...order, items: mergedItemsForSave }, skuMaster, computeTotals());
    const blob = pdfDoc.output("blob");
    const path = `Challan-${finalId}.pdf`;
    const pdfUrl = await uploadPdfToSupabase("challans", path, blob);
    await updateDoc(doc(db, "orders", orderId), { challanPdfUrl: pdfUrl });
    window.open(pdfUrl, "_blank");
  } catch (err) { alert("Failed to generate Challan"); }
};


// -------------------------
// SAVE CHANGES
// -------------------------
const saveChanges = async () => {
  try {
    const refOrder = doc(db, "orders", orderId);
    const totalsValue = computeTotals();
    let orderDateValue = order.orderDate;
    if (!orderDateValue && order.createdAt) orderDateValue = order.createdAt;

    await updateDoc(refOrder, {
      items: mergedItemsForSave,
      totalQty: totalsValue.totalQty,
	  totalAmount: totalstrip.grandTotal,
      ewayAmount: totalsValue.grandTotal, 
      address: address || "",
      vehicleNo: vehicleNo || "",
      orderDate: orderDateValue || "",
      updatedAt: serverTimestamp(),
    });

    alert("Changes saved. Order is still PENDING.");
  } catch (err) { alert("Failed to save changes"); }
};


const generateTempTripSheet = () => {
  try {
    const tempId = order.tempOrderId || order.displayOrderId || order.orderNo || orderId;
    const totalsTrip = computeTotalsTrip();
    const orderForPdf = { ...order, orderDate: getOrderDateForTripSheet() };
    const pdfDoc = generateTripSheetPdf(tempId, orderForPdf, items, skuMaster, totalsTrip, "Temp Trip Sheet - Only for Stock Picking");
    const blob = pdfDoc.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TempTripSheet-${tempId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) { alert("Error generating Temp Trip Sheet"); }
};


  if (loading || !order) return <Layout><div style={{ padding: 20 }}>Loading...</div></Layout>;


  return (
    <Layout>
      <div style={{ minHeight: "calc(100vh - 80px)", background: UI_THEME.bg }}>
        
        {/* ACTION BAR (Premium Glassmorphism) */}
        <div style={actionBarStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
            <h2 style={{ fontSize: "24px", fontWeight: 800, color: UI_THEME.text, margin: 0 }}>{order.partyName}</h2>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#f1f5f9", padding: "6px 16px", borderRadius: "12px", border: `1px solid ${UI_THEME.border}` }}>
              <span style={{ fontSize: "12px", fontWeight: 800, color: UI_THEME.muted, textTransform: "uppercase" }}>Vehicle:</span>
              <input 
                 value={vehicleNo} 
                 onChange={(e) => setVehicleno(e.target.value)} 
                 placeholder="KA51JG8749"
                 style={{ 
                   background: "transparent",
                   border: "none",
                   fontSize: "14px",
                   fontWeight: 700,
                   color: UI_THEME.text,
                   width: "120px",
                   outline: "none"
                 }} 
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button onClick={saveChanges} style={secondaryBtn} {...pressAnim}>
              💾 Save Changes
            </button>
            <button onClick={approveOrder} disabled={approving} style={primaryBtn} {...pressAnim}>
              {approving ? (approvingStep || "Approving...") : "🚀 Approve Order"}
            </button>
            <button onClick={generateTempTripSheet} style={utilityBtn} {...pressAnim}>
              📄 Temp TripSheet PDF
            </button>
          </div>
        </div>

        {/* --- MAIN PAGE CONTENT --- */}
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px" }}>
          
          {/* ORDER ITEMS TABLE CARD */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 800, color: UI_THEME.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Order Details
              </h3>
              <div style={{ fontSize: "14px", fontWeight: 600, color: UI_THEME.muted }}>
                Batch ID: <span style={{ color: UI_THEME.text }}>{displayOrderId}</span>
              </div>
            </div>

            <div style={{ overflowX: "auto", borderRadius: "12px", border: `1px solid ${UI_THEME.border}` }}>
              <table width="100%" style={{ borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ background: UI_THEME.bg, borderBottom: `2px solid ${UI_THEME.border}` }}>
                    <th style={{ padding: "16px", textAlign: "left", fontWeight: 800 }}>SKU</th>
                    <th style={{ padding: "16px", textAlign: "left", fontWeight: 800 }}>Description</th>
                    <th style={{ padding: "16px", textAlign: "center", fontWeight: 800 }}>Qty</th>
                    <th style={{ padding: "16px", textAlign: "right", fontWeight: 800 }}>MRP</th>
                    <th style={{ padding: "16px", textAlign: "right", fontWeight: 800 }}>Rate</th>
                    <th style={{ padding: "16px", textAlign: "right", fontWeight: 800 }}>Basic</th>
                    <th style={{ padding: "16px", textAlign: "right", fontWeight: 800 }}>GST</th>
                    <th style={{ padding: "16px", textAlign: "right", fontWeight: 800 }}>Total</th>
                    <th style={{ padding: "16px", textAlign: "center", fontWeight: 800 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {skuListOrdered.map((sku, idx) => {
                    const m = skuMaster[sku] || {};
                    const qty = itemsForUI[sku];
                    const rate = Number(m.Basic_Eway || 0);
                    const BASIC = Number(qty || 0) * rate;
                    const MRP = Number(m.mrp || 0);
                    const mrpTotal = Number(qty || 0) * MRP;
                    const GST = mrpTotal - (mrpTotal / (1 + ((Number(m.gst) || 0) / 100)));
                    const CESS = BASIC * ((Number(m.cess) || 0) / 100);
                    const ADDL = Number(qty || 0) * (Number(m.addlcess) || 0);
                    const REBATE = Number(qty || 0) * (Number(m.Rebate) || 0);
                    const totalVal = BASIC + GST + CESS + ADDL + REBATE;
                    
                    if (!activeSkus.includes(sku)) return null; // Show only active items by default
                    
                    return (
                       <tr key={sku} style={{ borderBottom: `1px solid ${UI_THEME.border}`, background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{ padding: "12px 16px", fontWeight: 700 }}>{sku}</td>
                        <td style={{ padding: "12px 16px", color: UI_THEME.muted }}>{m.Name || ""}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <input 
                            type="number" 
                            min="0" 
                            step="0.01" 
                            value={qty} 
                            onChange={(e) => updateQty(sku, e.target.value)} 
                            style={{ 
                              width: "70px", 
                              padding: "6px", 
                              borderRadius: "6px", 
                              border: `1px solid ${UI_THEME.border}`,
                              textAlign: "center",
                              fontWeight: 700 
                            }} 
                          />
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>{MRP.toFixed(2)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>{rate.toFixed(2)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>{BASIC.toFixed(2)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>{GST.toFixed(2)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: UI_THEME.accent }}>{totalVal.toFixed(2)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <button 
                            onClick={() => {
                              setActiveSkus(prev => prev.filter(s => s !== sku));
                              updateQty(sku, 0);
                            }}
                            style={{ 
                              background: "none", 
                              border: "none", 
                              color: UI_THEME.danger, 
                              cursor: "pointer",
                              fontSize: "18px",
                              padding: "4px"
                            }}
                            title="Remove Item"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Option to show zero items if needed by user - logic simplified here */}
                </tbody>
                <tfoot>
                  <tr style={{ background: UI_THEME.bg, fontWeight: 900, color: UI_THEME.text }}>
                    <td colSpan="2" style={{ padding: "20px 16px" }}>GRAND TOTAL</td>
                    <td style={{ padding: "20px 16px", textAlign: "center", fontSize: "16px" }}>
                      {Number(totals.totalQty).toFixed(2)}
                    </td>
                    <td colSpan="2" style={{ textAlign: "right" }}>—</td>
                    <td style={{ padding: "20px 16px", textAlign: "right" }}>
                      ₹{Number(totals.totalBasic).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: "20px 16px", textAlign: "right" }}>
                      ₹{Number(totals.totalGST).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: "20px 16px", textAlign: "right", color: "#4338ca", fontSize: "18px" }}>
                      ₹{Number(totalstrip.grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* --- REFINED ADD SKU DROPDOWN --- */}
            <div style={{ marginTop: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ flex: 1 }}>
                  <select 
                    value=""
                    onChange={(e) => {
                      const sku = e.target.value;
                      if (!sku) return;
                      const m = skuMaster[sku] || {};
                      const qty = window.prompt(`Enter Quantity for ${sku} (${m.Name || ""}):`, "1");
                      if (qty !== null && !isNaN(qty) && Number(qty) > 0) {
                        setActiveSkus(prev => [...prev, sku]);
                        updateQty(sku, qty);
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "14px 20px",
                      borderRadius: "14px",
                      border: `2px solid ${UI_THEME.border}`,
                      fontSize: "15px",
                      fontWeight: 700,
                      color: UI_THEME.text,
                      background: "#fff",
                      cursor: "pointer",
                      outline: "none",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
                    }}
                  >
                    <option value="">➕ ADD NEW SKU TO THIS ORDER...</option>
                    {remainingSkus.map(sku => (
                      <option key={sku} value={sku}>
                        {sku} — {skuMaster[sku]?.Name || ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p style={{ fontSize: "12px", color: UI_THEME.muted, marginTop: "8px", fontWeight: 600, marginLeft: "4px" }}>
                * Selecting an item will prompt for the quantity.
              </p>
            </div>
          </div>

          {/* Vehicle and Address Details removed and moved to top */}
        </div>

        {/* MODAL OVERLAY FOR OUTLET SELECTION */}
        {showOutletModal && (
          <div style={modalOverlayStyle}>
            <div style={{ ...modalContentStyle, position: "relative" }}>
              {/* Close Button at top right */}
              <button 
                onClick={() => setShowOutletModal(false)}
                style={{ 
                  position: "absolute", 
                  top: 20, 
                  right: 20, 
                  background: "#f1f5f9", 
                  border: "none", 
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  fontSize: 18, 
                  cursor: "pointer", 
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10
                }}
                title="Close"
              >
                ✕
              </button>
              
              <h3 style={{ marginTop: 0, fontSize: 18, fontWeight: 700 }}>Select Distribution Outlets</h3>
              <p style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
                Specify which outlets will receive splits from this order. Leave empty for no-split billing.
              </p>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#4a5568" }}>OUTLET LIST</span>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button 
                      onClick={() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const allValidIds = outlets
                          .filter(o => {
                             // Only block selection if the ANNUAL GST limit is hit
                             const isAnnualReached = (Number(o.annualUsed || 0)) >= (Number(o.annualLimit || 0)) && (Number(o.annualLimit) > 0);
                             return !isAnnualReached;
                          })
                          .map(o => o.id);
                        setSelectedOutletIds(allValidIds);
                      }}
                      style={{ background: "none", border: "none", color: "#166534", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "0 4px" }}
                    >
                      Select All
                    </button>
                    <span style={{ color: "#cbd5e0" }}>|</span>
                    <button 
                      onClick={() => setSelectedOutletIds([])}
                      style={{ background: "none", border: "none", color: "#c53030", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "0 4px" }}
                    >
                      Deselect All
                    </button>
                  </div>
              </div>
              
              <div style={{ maxHeight: "350px", overflowY: "auto", border: "1px solid #edf2f7", borderRadius: 10, padding: 8 }}>
                {outlets.map(o => {
                  const isAnnualReached = (Number(o.annualUsed || 0)) >= (Number(o.annualLimit || 0)) && (Number(o.annualLimit) > 0);
                  const isDisabled = isAnnualReached;

                  return (
                    <label key={o.id} style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      padding: "12px", 
                      background: isDisabled ? "#fff5f5" : (selectedOutletIds.includes(o.id) ? "#f0fff4" : "#fff"),
                      borderBottom: "1px solid #edf2f7",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      borderRadius: 8,
                      marginBottom: 6,
                      opacity: isDisabled ? 0.8 : 1
                    }}>
                      <input 
                        type="checkbox"
                        checked={selectedOutletIds.includes(o.id)}
                        disabled={isDisabled}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedOutletIds([...selectedOutletIds, o.id]);
                          else setSelectedOutletIds(selectedOutletIds.filter(id => id !== o.id));
                        }}
                        style={{ width: 18, height: 18, marginRight: 15 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: isDisabled ? "#c53030" : "#2d3748" }}>{o.outletName}</div>
                        <div style={{ fontSize: 11, color: "#718096", marginTop: 2 }}>
                          Per-Invoice: ₹{Number(o.dailyInvoiceLimit).toLocaleString()} | Annual Used: ₹{Number(o.annualUsed || 0).toLocaleString()} / ₹{Number(o.annualLimit || 0).toLocaleString()}
                        </div>
                      </div>
                      {isDisabled && <div style={{ fontSize: 10, fontWeight: 800, color: "#c53030" }}>LIMITS REACHED</div>}
                    </label>
                  );
                })}
              </div>

              {/* CALCULATION FOR BUTTON */}
              {(() => {
                const today = new Date().toISOString().slice(0, 10);
                const selectedOutlets = outlets.filter(o => selectedOutletIds.includes(o.id));
                const totalAvailableLimit = Math.round(selectedOutlets.reduce((acc, o) => {
                  const perInvoice = Number(o.dailyInvoiceLimit) || 0;
                  const annLimit = Number(o.annualLimit) || 0;
                  const annUsed = Number(o.annualUsed) || 0;
                  const remainingAnn = Math.max(0, annLimit - annUsed);
                  
                  // Limit for this specific bill is the smaller of per-invoice cap or annual remainder
                  const effectiveRemaining = Math.min(perInvoice, remainingAnn);
                  return acc + effectiveRemaining;
                }, 0));
                
                const billValue = Math.round(totalstrip.grandTotal || 0);
                const limitWarning = billValue > totalAvailableLimit;

                return (
                  <div style={{ marginTop: 24 }}>
                    <button 
                      onClick={approveOrder} 
                      style={{ 
                        width: "100%", 
                        padding: "14px", 
                        background: "#166534", 
                        color: "#fff", 
                        border: "none", 
                        borderRadius: 8, 
                        fontWeight: 700, 
                        cursor: "pointer", 
                        fontSize: 14,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2
                      }}
                    >
                      <span>Proceed with Salesman ({selectedOutletIds.length})</span>
                      <div style={{ fontSize: 11, opacity: 1, fontWeight: 500, marginTop: 4 }}>
                        <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", width: "100%", margin: "4px 0" }}></div>
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "30px" }}>
                          <span style={{ opacity: 0.9 }}>Total Limit: ₹{totalAvailableLimit.toLocaleString()}</span>
                          <span style={{ color: limitWarning ? "#ff9b9b" : "#fff", fontWeight: limitWarning ? 800 : 500 }}>
                            Bill Value: ₹{billValue.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* --- PREMIUM APPROVAL PROGRESS OVERLAY --- */}
        {approving && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(10, 15, 30, 0.85)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}>
            <div style={{
              width: "400px",
              background: "#fff",
              padding: "40px",
              borderRadius: "24px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              textAlign: "center",
              animation: "slideUp 0.4s ease-out"
            }}>
              <div style={{ 
                marginBottom: "24px",
                fontSize: "48px"
              }}>
                {approvalProgress < 100 ? "⚙️" : "✅"}
              </div>
              
              <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#1a202c", marginBottom: "8px" }}>
                {approvalProgress < 100 ? "Processing Order" : "Finalizing..."}
              </h2>
              
              <p style={{ color: "#718096", fontSize: "14px", marginBottom: "24px" }}>
                {approvingStep}
              </p>

              {/* Progress Bar Container */}
              <div style={{
                height: "12px",
                width: "100%",
                background: "#edf2f7",
                borderRadius: "10px",
                overflow: "hidden",
                marginBottom: "12px",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)"
              }}>
                <div style={{
                  height: "100%",
                  width: `${approvalProgress}%`,
                  background: "linear-gradient(90deg, #3182ce 0%, #63b3ed 100%)",
                  borderRadius: "10px",
                  transition: "width 0.3s ease-out",
                  boxShadow: "0 0 15px rgba(66, 153, 225, 0.5)"
                }}></div>
              </div>

              <div style={{ 
                fontSize: "18px", 
                fontWeight: 800, 
                color: "#2b6cb0",
                fontVariantNumeric: "tabular-nums"
              }}>
                {approvalProgress}%
              </div>

              <div style={{ marginTop: "32px", fontSize: "11px", color: "#a0aec0", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Please do not refresh or close this window
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </Layout>
  );
}

export default ApproveOrder;
